import React from 'react';
import { MenuItem } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Trash2, Lightbulb, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { AppOutlinedTextField } from '@/components/shared/AppOutlinedTextField';
import { FoodCategoryPortion } from '@/types/food-category';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';

const GRAM_SERVING_PATTERN = /^(g|gram|grams)$/i;

function isGramServing(value: unknown): boolean {
  return GRAM_SERVING_PATTERN.test(String(value ?? '').trim());
}

/** Outlined fields aligned with Portion Sizes frame (Figma DEV 1:3078). */
const portionSelectSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    minHeight: 56,
    borderRadius: '4px',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#52B2D7',
    },
  },
  '& .MuiSelect-select': {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '14px',
    paddingRight: '40px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(0, 0, 0, 0.6)',
    '&.Mui-focused': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
  },
};

const portionNumberSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    minHeight: 56,
    borderRadius: '4px',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#52B2D7',
    },
  },
  '& input': {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: '1rem',
    lineHeight: '24px',
    letterSpacing: '0.15px',
    padding: '16px 14px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(0, 0, 0, 0.6)',
    '&.Mui-focused': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
  },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
};

const portionInputClass =
  'h-9 min-h-9 rounded border border-[#eaeaea] bg-white pl-2 pr-1 text-center text-sm leading-5 text-[#364153] shadow-none focus-visible:ring-1 focus-visible:ring-[#52B2D7] focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&_input[type=number]]:appearance-none [&_input[type=number]]:[-moz-appearance:textfield]';
const portionReadOnlyInputClass =
  'cursor-default border-[#bfe5f2] bg-[#f8fcfe] text-[#4b6472] placeholder:text-[#6eaec6] focus-visible:ring-0';

/** Horizontal gap between portion name / weight / volume columns (Figma ~82px). */
const portionColGap = 'ml-3 lg:ml-8 xl:ml-[82px]';
const portionWeightColClass = 'w-[86px] lg:w-[96px] xl:w-[100px]';
const portionVolumeColClass = 'w-[130px] lg:w-[150px] xl:w-[170px]';
const portionQuantityColClass = 'w-[82px] lg:w-[92px] xl:w-[96px]';
const portionWeightInputWidthClass = 'w-[74px] lg:w-[82px] xl:w-[85px]';
const portionVolumeInputWidthClass = 'w-[118px] lg:w-[138px] xl:w-[150px]';
const portionQuantityInputWidthClass = 'w-[70px] lg:w-[78px] xl:w-[82px]';
const actionSpacerClass = 'hidden md:block w-6 lg:w-8 xl:w-10';

interface RecipePortionSizesSectionProps {
  portionSizeId: string;
  portionSizes: FoodCategoryPortion[];
  defaultServingGrams: number | '';
  portionDensityGPerMl: number | '';
  customPortionName: string;
  customPortionGrams: number | '';
  customPortionMl: number | '';
  onPortionSizeChange: (value: string) => void;
  onDefaultServingGramsChange: (value: number | '') => void;
  onPortionDensityChange: (value: number | '') => void;
  onPortionWeightChange: (servingId: string, value: number | '') => void;
  onPortionMlChange: (servingId: string, value: number | '') => void;
  onPortionQuantityChange: (servingId: string, value: number | '') => void;
  onCustomPortionNameChange: (value: string) => void;
  onCustomPortionGramsChange: (value: number | '') => void;
  onCustomPortionMlChange: (value: number | '') => void;
  onAddCustomPortion: () => void;
  onRemovePortion: (servingId: string) => void;
  getPortionOptionValue: (portion: FoodCategoryPortion, index: number) => string;
}

function ColumnHeaderGroup({
  leftLabel,
}: {
  leftLabel: string;
}) {
  return (
    <div
      className="flex w-full min-w-0 items-center px-4 py-2"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <div className="min-w-0 max-w-[240px] lg:max-w-[300px] xl:max-w-[350px] flex-1">
        <span className="text-xs font-normal uppercase leading-4 tracking-[0.3px] text-[#99a1af]">
          {leftLabel}
        </span>
      </div>
      <div className={`flex shrink-0 items-center justify-center ${portionColGap} ${portionWeightColClass}`}>
        <span className="whitespace-nowrap text-center text-xs font-normal uppercase leading-3 tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
          Weight(Grams)
        </span>
      </div>
      <div className={`flex shrink-0 items-center justify-center ${portionColGap} ${portionQuantityColClass}`}>
        <span className="text-center text-xs font-normal uppercase leading-3 tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
          Quantity
        </span>
      </div>
      <div className={`flex shrink-0 items-center justify-center ${portionColGap} ${portionVolumeColClass}`}>
        <span className="whitespace-nowrap text-center text-xs font-normal uppercase leading-3 tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
          Volume(ml, optional)
        </span>
      </div>
      <div className="ml-auto flex shrink-0 items-center">
        <div className={actionSpacerClass} aria-hidden />
        <div className="w-7" aria-hidden />
      </div>
    </div>
  );
}

export const RecipePortionSizesSection: React.FC<RecipePortionSizesSectionProps> = ({
  portionSizeId,
  portionSizes,
  defaultServingGrams,
  portionDensityGPerMl,
  customPortionName,
  customPortionGrams,
  customPortionMl,
  onPortionSizeChange,
  onDefaultServingGramsChange,
  onPortionDensityChange,
  onPortionWeightChange,
  onPortionMlChange,
  onPortionQuantityChange,
  onCustomPortionNameChange,
  onCustomPortionGramsChange,
  onCustomPortionMlChange,
  onAddCustomPortion,
  onRemovePortion,
  getPortionOptionValue,
}) => {
  const hasDensity = typeof portionDensityGPerMl === 'number' && portionDensityGPerMl > 0;

  return (
    <div className="w-full" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="mb-10 flex items-start gap-3 rounded-lg border border-[#3f9fee] p-3">
        <Lightbulb className="mt-0.5 size-[18px] shrink-0 text-[#3f9fee]" strokeWidth={1.75} />
        <p className="flex-1 text-xs font-normal leading-[1.45] text-[#0a0a0a]">
          Suggested portion sizes are based on dish type. At least one portion size is required. Use grams by default,
          or enter density (g/ml) to enable ml fields and recalculate grams from volume.
        </p>
      </div>

      <div className="mb-[27px] grid grid-cols-1 gap-x-1.5 gap-y-4 md:grid-cols-3">
        <AppOutlinedTextField
          select
          id="portion-size"
          label="Default Portion Sizes"
          value={portionSizeId || ''}
          onChange={(e) => onPortionSizeChange((e.target.value as string) || '')}
          fullWidth
          size="small"
          sx={portionSelectSx}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="" sx={{ fontSize: '14px', color: '#94a3b8' }}>
            {portionSizes.length > 0 ? 'Select default portion size' : 'Add a portion size first'}
          </MenuItem>
          {portionSizes.map((portion, index) => {
            const optionValue = getPortionOptionValue(portion, index);
            const displayName = formatUnderscoreDisplayLabel(portion.name || optionValue || `Portion ${index + 1}`);
            return (
              <MenuItem key={optionValue} value={optionValue} sx={{ fontSize: '14px' }}>
                {displayName}
              </MenuItem>
            );
          })}
        </AppOutlinedTextField>

        <div>
          <AppOutlinedTextField
            id="default-serving-grams"
            label="Default Portion Weight"
            type="number"
            value={defaultServingGrams === '' ? '' : defaultServingGrams}
            placeholder="e.g. 350"
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                onDefaultServingGramsChange('');
                if (portionSizeId) onPortionWeightChange(portionSizeId, '');
                return;
              }

              const parsed = parseFloat(value);
              if (!isNaN(parsed) && parsed >= 0) {
                onDefaultServingGramsChange(parsed);
                if (portionSizeId) onPortionWeightChange(portionSizeId, parsed);
              }
            }}
            fullWidth
            size="small"
            inputProps={{ 
              min: 0
            }}
            InputProps={{ readOnly: true }}
            onWheel={(e) => e.currentTarget.blur()}
          />
        
        </div>

        <AppOutlinedTextField
          id="portion-density"
          label="Density (g/ml, optional)"
          type="number"
          value={portionDensityGPerMl === '' ? '' : portionDensityGPerMl}
          placeholder="e.g. 1.03"
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') {
              onPortionDensityChange('');
              return;
            }

            const parsed = parseFloat(value);
            if (!isNaN(parsed) && parsed > 0) {
              onPortionDensityChange(parsed);
            }
          }}
          fullWidth
          size="small"
          inputProps={{ min: 0, step: 0.0001 }}
          onWheel={(e) => e.currentTarget.blur()}
          sx={portionNumberSx}
        />
      </div>

      <div className="overflow-hidden rounded-[14px] border border-[rgba(0,0,0,0.06)] bg-white">
        <div
          className="flex h-[45px] items-center px-4"
          style={{
            background: 'linear-gradient(90deg, rgb(50, 184, 222) 0%, rgb(32, 174, 220) 100%)',
          }}
        >
          <span className="text-sm font-semibold leading-5 text-white">Portion Size</span>
        </div>

        <div className="flex flex-col bg-white">
          <ColumnHeaderGroup leftLabel="Default portion size" />

          <div className="flex flex-col gap-0.5 px-0 pb-0">
            {portionSizes.map((portion, index) => {
              if (portion.isCustom) return null;
              const optionValue = getPortionOptionValue(portion, index);
              const displayName = formatUnderscoreDisplayLabel(portion.name || optionValue || `Portion ${index + 1}`);
              const servingIdentifier = portion.servingId ?? portion.id ?? portion.name ?? optionValue;
              const isGramRow = isGramServing(servingIdentifier);
              const currentWeight = portion.gramsOverride ?? portion.weightG;
              const currentQuantity = portion.quantity ?? 1;
              const currentMl = portion.volumeMl;
              const suggestedWeight = portion.suggestedWeightG;
              const hasMlWithoutGrams =
                currentMl != null && currentMl > 0 && (currentWeight == null || currentWeight <= 0);
              const isDefaultRow = optionValue === portionSizeId;

              return (
                <div key={`portion-weight-${optionValue}`}>
                  <div className="flex w-full min-w-0 items-center rounded-lg bg-[#f1f8ff] px-4 py-2.5">
                    <div className="min-w-0 max-w-[240px] lg:max-w-[300px] xl:max-w-[350px] flex-1">
                      <div className="rounded-lg bg-white px-4 py-2 text-sm font-normal leading-5 text-[#364153]/80">
                        {displayName}
                        {isDefaultRow ? (
                          <span className="ml-1 text-xs font-semibold uppercase text-[#20aedc]">(Default)</span>
                        ) : null}
                      </div>
                    </div>
                    <div className={`flex shrink-0 items-stretch justify-center ${portionColGap} ${portionWeightColClass}`}>
                        <Input
                          id={`portion-weight-${optionValue}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={isGramRow ? 1 : currentWeight == null ? '' : currentWeight}
                          placeholder={suggestedWeight != null ? `${suggestedWeight}` : '0'}
                          onChange={
                            isGramRow
                              ? undefined
                              : (e) => {
                                  const value = e.target.value;
                                  if (value === '') {
                                    onPortionWeightChange(optionValue, '');
                                    return;
                                  }
                                  const parsed = parseFloat(value);
                                  if (!isNaN(parsed) && parsed >= 0) {
                                    onPortionWeightChange(optionValue, parsed);
                                  }
                                }
                          }
                          readOnly={isGramRow}
                          aria-readonly={isGramRow}
                          className={`${portionInputClass} ${portionWeightInputWidthClass} ${isGramRow ? portionReadOnlyInputClass : ''}`}
                          onWheel={isGramRow ? undefined : (e) => e.currentTarget.blur()}
                        />
                    </div>
                    <div className={`flex shrink-0 items-stretch justify-center ${portionColGap} ${portionQuantityColClass}`}>
                        <Input
                          id={`portion-quantity-${optionValue}`}
                          type="number"
                          min="1"
                          step="1"
                          value={currentQuantity}
                          placeholder="1"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              onPortionQuantityChange(optionValue, '');
                              return;
                            }
                            const parsed = parseFloat(value);
                            if (!isNaN(parsed) && parsed > 0) {
                              onPortionQuantityChange(optionValue, parsed);
                            }
                          }}
                          className={`${portionInputClass} ${portionQuantityInputWidthClass}`}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                    </div>
                    <div className={`flex shrink-0 items-stretch justify-center ${portionColGap} ${portionVolumeColClass}`}>
                        <Input
                          id={`portion-ml-${optionValue}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={currentMl == null ? '' : currentMl}
                          placeholder={hasDensity ? '15' : 'Density first'}
                          readOnly={!hasDensity}
                          aria-readonly={!hasDensity}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              onPortionMlChange(optionValue, '');
                              return;
                            }
                            const parsed = parseFloat(value);
                            if (!isNaN(parsed) && parsed >= 0) {
                              onPortionMlChange(optionValue, parsed);
                            }
                          }}
                          className={`${portionInputClass} ${portionVolumeInputWidthClass} ${!hasDensity ? portionReadOnlyInputClass : ''}`}
                          onWheel={hasDensity ? (e) => e.currentTarget.blur() : undefined}
                        />
                    </div>
                    <div className="ml-auto flex shrink-0 items-center">
                      <div className={actionSpacerClass} aria-hidden />
                      <div className="flex w-7 items-center justify-center">
                        <button
                          type="button"
                          className="flex size-5 items-center justify-center text-[#f87171] transition-colors hover:text-[#ef4444] disabled:opacity-50"
                          disabled={portionSizes.length <= 1}
                          onClick={() => onRemovePortion(optionValue)}
                          aria-label={`Remove ${displayName}`}
                        >
                          <Trash2 className="size-5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </div>
                  {(isGramRow || hasMlWithoutGrams || portionSizes.length <= 1) && (
                    <div className="px-4 pb-1 text-xs text-red-500">
                      {isGramRow
                        ? 'Gram serving is fixed at 1g.'
                        : hasMlWithoutGrams
                          ? 'Enter grams before adding an ml value.'
                          : 'At least one portion size must remain.'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <ColumnHeaderGroup leftLabel="Custom portion size" />

          <div className="flex flex-col gap-0.5 px-0">
            {portionSizes.map((portion, index) => {
              if (!portion.isCustom) return null;
              const optionValue = getPortionOptionValue(portion, index);
              const displayName = formatUnderscoreDisplayLabel(portion.name || optionValue || `Portion ${index + 1}`);
              const currentWeight = portion.gramsOverride ?? portion.weightG;
              const currentQuantity = portion.quantity ?? 1;
              const currentMl = portion.volumeMl;
              const suggestedWeight = portion.suggestedWeightG;
              const hasMlWithoutGrams =
                currentMl != null && currentMl > 0 && (currentWeight == null || currentWeight <= 0);
              const isDefaultRow = optionValue === portionSizeId;

              return (
                <div key={`custom-portion-weight-${optionValue}`}>
                  <div className="flex w-full min-w-0 items-center rounded-lg bg-[#f1f8ff] px-4 py-2.5">
                    <div className="min-w-0 max-w-[240px] lg:max-w-[300px] xl:max-w-[350px] flex-1">
                      <div className="rounded-lg bg-white px-4 py-2 text-sm font-normal leading-5 text-[#364153]/80">
                        {displayName}
                        {isDefaultRow ? (
                          <span className="ml-1 text-xs font-semibold uppercase text-[#20aedc]">(Default)</span>
                        ) : null}
                      </div>
                    </div>
                    <div className={`flex shrink-0 items-stretch justify-center ${portionColGap} ${portionWeightColClass}`}>
                        <Input
                          id={`custom-portion-weight-${optionValue}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={currentWeight == null ? '' : currentWeight}
                          placeholder={suggestedWeight != null ? `${suggestedWeight}` : '0'}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              onPortionWeightChange(optionValue, '');
                              return;
                            }
                            const parsed = parseFloat(value);
                            if (!isNaN(parsed) && parsed >= 0) {
                              onPortionWeightChange(optionValue, parsed);
                            }
                          }}
                          className={`${portionInputClass} ${portionWeightInputWidthClass}`}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                    </div>
                    <div className={`flex shrink-0 items-stretch justify-center ${portionColGap} ${portionQuantityColClass}`}>
                        <Input
                          id={`custom-portion-quantity-${optionValue}`}
                          type="number"
                          min="1"
                          step="1"
                          value={currentQuantity}
                          placeholder="1"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              onPortionQuantityChange(optionValue, '');
                              return;
                            }
                            const parsed = parseFloat(value);
                            if (!isNaN(parsed) && parsed > 0) {
                              onPortionQuantityChange(optionValue, parsed);
                            }
                          }}
                          className={`${portionInputClass} ${portionQuantityInputWidthClass}`}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                    </div>
                    <div className={`flex shrink-0 items-stretch justify-center ${portionColGap} ${portionVolumeColClass}`}>
                        <Input
                          id={`custom-portion-ml-${optionValue}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={currentMl == null ? '' : currentMl}
                          placeholder={hasDensity ? '15' : 'Density first'}
                          readOnly={!hasDensity}
                          aria-readonly={!hasDensity}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              onPortionMlChange(optionValue, '');
                              return;
                            }
                            const parsed = parseFloat(value);
                            if (!isNaN(parsed) && parsed >= 0) {
                              onPortionMlChange(optionValue, parsed);
                            }
                          }}
                          className={`${portionInputClass} ${portionVolumeInputWidthClass} ${!hasDensity ? portionReadOnlyInputClass : ''}`}
                          onWheel={hasDensity ? (e) => e.currentTarget.blur() : undefined}
                        />
                    </div>
                    <div className="ml-auto flex shrink-0 items-center">
                      <div className={actionSpacerClass} aria-hidden />
                      <div className="flex w-7 items-center justify-center">
                        <button
                          type="button"
                          className="flex size-5 items-center justify-center text-[#f87171] transition-colors hover:text-[#ef4444] disabled:opacity-50"
                          disabled={portionSizes.length <= 1}
                          onClick={() => onRemovePortion(optionValue)}
                          aria-label={`Remove ${displayName}`}
                        >
                          <Trash2 className="size-5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </div>
                  {(hasMlWithoutGrams || portionSizes.length <= 1) && (
                    <div className="px-4 pb-1 text-xs text-red-500">
                      {hasMlWithoutGrams
                        ? 'Enter grams before adding an ml value.'
                        : 'At least one portion size must remain.'}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="flex w-full min-w-0 items-center rounded-lg bg-[#f1f8ff] px-4 py-2.5">
              <div className="min-w-0 max-w-[240px] lg:max-w-[300px] xl:max-w-[350px] flex-1">
                <Input
                  value={customPortionName}
                  onChange={(e) => onCustomPortionNameChange(e.target.value)}
                  placeholder="e.g. Large Bowl"
                  className="h-9 rounded-lg border-0 bg-white px-4 py-2 text-sm leading-5 text-[#364153] placeholder:text-[#99a1af] shadow-none focus-visible:ring-1 focus-visible:ring-[#52B2D7]"
                />
              </div>
              <div className={`flex shrink-0 items-stretch justify-center ${portionColGap} ${portionWeightColClass}`}>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={customPortionGrams === '' ? '' : customPortionGrams}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      onCustomPortionGramsChange('');
                      return;
                    }
                    const parsed = parseFloat(value);
                    if (!isNaN(parsed) && parsed >= 0) {
                      onCustomPortionGramsChange(parsed);
                    }
                  }}
                  placeholder="15"
                  className={`${portionInputClass} ${portionWeightInputWidthClass}`}
                />
              </div>
              <div className={`flex shrink-0 items-stretch justify-center ${portionColGap} ${portionQuantityColClass}`}>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={1}
                  readOnly
                  aria-readonly
                  className={`${portionInputClass} ${portionQuantityInputWidthClass} ${portionReadOnlyInputClass}`}
                />
              </div>
              <div className={`flex shrink-0 items-stretch justify-center ${portionColGap} ${portionVolumeColClass}`}>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={customPortionMl === '' ? '' : customPortionMl}
                  readOnly={!hasDensity}
                  aria-readonly={!hasDensity}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      onCustomPortionMlChange('');
                      return;
                    }
                    const parsed = parseFloat(value);
                    if (!isNaN(parsed) && parsed >= 0) {
                      onCustomPortionMlChange(parsed);
                    }
                  }}
                  placeholder={hasDensity ? '15' : 'Density first'}
                  className={`${portionInputClass} ${portionVolumeInputWidthClass} ${!hasDensity ? portionReadOnlyInputClass : ''}`}
                />
              </div>
              <div className="ml-auto flex shrink-0 items-center">
                <div className={actionSpacerClass} aria-hidden />
                <div className="w-7" aria-hidden />
              </div>
            </div>
            {customPortionMl !== '' && !hasDensity && (
              <div className="px-4 pb-2 text-xs text-red-500">Enter density before adding an ml value.</div>
            )}
          </div>

          <div className="flex h-[58px] items-center justify-center border-b border-[#e5e7eb] px-4 py-4">
            <button
              type="button"
              onClick={onAddCustomPortion}
              disabled={!customPortionName}
              className="flex items-center gap-[5px] text-sm font-medium leading-5 text-[#20aedc] transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <Plus className="size-3.5 shrink-0" strokeWidth={2.5} />
              Add Custom Portion Size
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
